import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import ArrowSvg from '@/shared/assets/icons/ui-kit/angle-down.svg';
import CloseSvg from '@/shared/assets/icons/ui-kit/close-modal.svg';
import { Box } from '@/shared/ui/box';
import { BoxProps } from '@/shared/ui/box/box.types';
import { Chip } from '@/shared/ui/chip';
import { Icon } from '@/shared/ui/icon';
import { Text } from '@/shared/ui/text';

import { renderFilter } from './table-filters.renderers';
import * as Styled from './table-filters.styles';
import { TableFilter, TableFiltersToggle } from './table-filters.types';

type TableFiltersProps = {
	filters?: TableFilter[];
	onChange?: (id: string, value: unknown) => void;
	containerProps?: BoxProps;
	wrapperProps?: BoxProps;
	fixedFilters?: string[];
	toggle?: TableFiltersToggle;
};

const TableFiltersComponent = ({
	filters = [],
	onChange,
	containerProps,
	wrapperProps,
	fixedFilters = [],
	toggle,
}: TableFiltersProps) => {
	const {
		text: {
			colors: { secondary },
		},
	} = useTheme();
	const { t } = useTranslation();

	if (!filters?.length) return null;

	const hasToggle = Boolean(toggle);
	const isOpen = toggle?.isOpen ?? false;
	const fixedSet = new Set(fixedFilters ?? []);
	const fixedItems = filters.filter((filter) => fixedSet.has(filter.id));
	const extraFilters = filters.filter((filter) => !fixedSet.has(filter.id));

	const handleToggle = () => {
		if (!toggle) return;

		toggle.onToggle(!toggle.isOpen);
	};

	return (
		<Box direction="column" gap={12} width="100%" padding="24px" {...containerProps}>
			{fixedItems.length > 0 && (
				<Styled.FiltersWrapper gap={12} align="center" flexWrap="wrap" width="100%" {...wrapperProps}>
					{fixedItems.map((filter) => renderFilter(filter, { onChange }))}
				</Styled.FiltersWrapper>
			)}
			{hasToggle && (
				<Box justify="space-between" width="280px" {...toggle?.controlsProps}>
					<Box align="center" gap={6} onClick={handleToggle} width="fit-content" padding="7px 12px 7px 0px">
						<Text label={isOpen ? t('hide.filters.button') : t('show.filters.button')} color="secondary" />
						<Styled.Arrow Svg={ArrowSvg} fill={secondary} open={isOpen} />
					</Box>
					{Boolean(toggle?.appliedCount) && !isOpen && (
						<Chip
							label={toggle?.chipLabel ?? ''}
							endIcon={
								toggle?.onReset && <Icon Svg={CloseSvg} fill={secondary} cursor="pointer" onClick={toggle.onReset} />
							}
						/>
					)}
				</Box>
			)}
			{(!hasToggle || isOpen) && extraFilters.length > 0 && (
				<Styled.FiltersWrapper gap={12} align="center" flexWrap="wrap" width="100%" {...wrapperProps}>
					{extraFilters.map((filter) => renderFilter(filter, { onChange }))}
				</Styled.FiltersWrapper>
			)}
		</Box>
	);
};

export default memo(TableFiltersComponent);
